// Texts of the public quote request (Screens/Quote: QuoteDetails, QuoteOptions,
// QuoteContact and QuoteComponents) that the order checkout doesn't already
// have. Keys start with "quote.". The English is the quote's own copy, word
// for word; the rest of the quote uses the checkout's keys (details.,
// delivery., contact., field., button., missing.).
import { NoTranslate } from "../../../NewComponents/TranslationSafe";

const en = {
  // Step names in the header ("1. Details" uses step.details)
  "quote.step.services": "Services",
  "quote.step.contact": "Your Contact",
  "quote.step.created": "Quote Created",

  // Details: the email comes first, as in the order checkout (title and
  // error from details.emailTitle / details.emailError)
  "quote.details.emailText": "We'll email you as soon as your quote is ready.",

  "quote.button.continueServices": "Continue to Services",
  "quote.button.backOptions": "Back to Options",
  "quote.button.complete": "Complete",
  "quote.button.completing": "Completing...",

  // The phone bar, where the order shows its total
  "quote.bar.label": "Your quote",
  "quote.bar.free": "Free",
  "quote.bar.noObligation": "No obligation to buy",
  "quote.bar.files": ({ n }) => `${n} ${Number(n) === 1 ? "file" : "files"}`,

  // The right-hand column
  "quote.expect.title": "WHAT TO EXPECT",
  "quote.expect.text": "We’ll respond quickly with a formal quote covering the cost and turnaround time for your translation project.",
  "quote.expect.free": "Free quote, no obligation to buy",
  "quote.expect.final": "Final pricing with no surprises",
  "quote.expect.turnaround": "Estimated turnaround time",
  "quote.expect.convert": "Quick convert to order when ready",
  "quote.expect.guaranteed": "Quote is guaranteed for 30 days",

  // Services
  "quote.services.expeditedText": "Your order will be prioritized and turnaround time for digital delivery reduced by 50%.",
  "quote.services.expeditedPlusText": "Your order will be prioritized and turnaround time for digital delivery reduced by 83.33%.",
  "quote.services.similarClients": "We found similar clients in your account. Would you like to select one of them for this order?",
  "quote.services.clientQuotes": ({ n }) => `(${n} quotes)`,

  // Contact
  "quote.discount.title": "Discount Code",
  "quote.discount.applying": "Applying...",
  "quote.discount.noLongerValid": "That code is no longer valid. Please, try another one.",
  "quote.discount.ownTeam": "That's your team's code for its clients. It doesn't work on your own quotes.",
  "quote.discount.alreadyUsed": "You already used that code. It's one use per client.",
  "quote.discount.notFound": "That code doesn't exist. Please, try another one.",
  "quote.discount.failed": "There was an error applying the code. Please, try again.",
  "quote.discount.tooMany": "Too many codes tried. Please wait a while and try again.",
  "quote.error.number": "We couldn't get a quote number from our servers. Check your internet connection and try again. If it happens again, write to support@everbary.com.",
  "quote.error.rejected": "Our system didn't accept your request's details. This is a problem on our side, and trying again won't fix it. Write to support@everbary.com with your file and we'll prepare your quote.",
  "quote.error.connection": "We couldn't reach our servers. Check your internet connection and try again. What you entered is still here.",
  "quote.error.other": "Something went wrong on our side. Please try again. If it happens again, write to support@everbary.com with your file and we'll prepare your quote.",
  "quote.error.clock": "The date or time on your device is wrong, so our system can't accept your request. Correct it in your device's settings and try again.",
  "quote.error.address": "An error ocurred while saving the address. Unfortunately, the address couldn't be saved.",
  // A file of the request whose upload is no longer in Storage (Data/Functions/orderFiles.js)
  "quote.missingFiles": ({ names }) => {
    const quoted = names.map((name) => `"${name}"`);
    const list = quoted.length > 1 ? `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}` : quoted[0];
    const many = names.length > 1;
    return `${list} ${many ? "are" : "is"} no longer uploaded, so we removed ${many ? "them" : "it"} from your quote request. Please upload ${many ? "them" : "it"} again.`;
  },

  // The confirmation, in place of the contact form
  "quote.done.title": ({ name }) => (name ? <>Thanks, <NoTranslate>{name}</NoTranslate>. Your quote request is in.</> : "Thanks. Your quote request is in."),
  "quote.done.number": ({ number }) => <>Quote <NoTranslate as="b">#{number}</NoTranslate></>,
  "quote.done.text": "You can track its status on the quote page in your account. We'll send you an email notification as soon as the cost is calculated.",
  "quote.done.goToQuote": "Go to Quote",

  // Going on with a saved quote request (?draft=, Data/Functions/draftQuote.js)
  "quote.draft.sentTitle": "Already sent",
  "quote.draft.sentText": ({ number }) => `Quote request #${number} was already sent. Here it is.`,
  "quote.draft.signInTitle": "Sign in to continue",
  "quote.draft.signInText": ({ number }) => `Quote request #${number} was started from an account. Sign in with that account to continue it, or start a new one here.`,
};

const es = {
  "quote.step.services": "Servicios",
  "quote.step.contact": "Sus datos",
  "quote.step.created": "Cotización enviada",

  "quote.details.emailText": "Le avisaremos por correo apenas esté lista su cotización.",

  "quote.button.continueServices": "Continuar a Servicios",
  "quote.button.backOptions": "Volver a Servicios",
  "quote.button.complete": "Pedir cotización",
  "quote.button.completing": "Enviando...",

  "quote.bar.label": "Su cotización",
  "quote.bar.free": "Gratis",
  "quote.bar.noObligation": "Sin compromiso de compra",
  "quote.bar.files": ({ n }) => `${n} ${Number(n) === 1 ? "archivo" : "archivos"}`,

  "quote.expect.title": "QUÉ ESPERAR",
  "quote.expect.text": "Le respondemos rápido con una cotización formal que indica el costo y el plazo de entrega de su traducción.",
  "quote.expect.free": "Cotización gratis, sin compromiso de compra",
  "quote.expect.final": "Precio final, sin sorpresas",
  "quote.expect.turnaround": "Plazo de entrega estimado",
  "quote.expect.convert": "Se convierte en pedido en un paso, cuando usted quiera",
  "quote.expect.guaranteed": "El precio cotizado se mantiene por 30 días",

  "quote.services.expeditedText": "Su pedido tiene prioridad y el plazo de la entrega digital se reduce un 50%.",
  "quote.services.expeditedPlusText": "Su pedido tiene prioridad y el plazo de la entrega digital se reduce un 83.33%.",
  "quote.services.similarClients": "Encontramos clientes parecidos en su cuenta. ¿Desea elegir uno de ellos para esta cotización?",
  "quote.services.clientQuotes": ({ n }) => `(${n} ${Number(n) === 1 ? "cotización" : "cotizaciones"})`,

  "quote.discount.title": "Código de descuento",
  "quote.discount.applying": "Aplicando...",
  "quote.discount.noLongerValid": "Ese código ya no es válido. Pruebe con otro.",
  "quote.discount.ownTeam": "Ese es el código de su equipo para sus clientes. No sirve para sus propias cotizaciones.",
  "quote.discount.alreadyUsed": "Ya usó ese código. Es de un solo uso por cliente.",
  "quote.discount.notFound": "Ese código no existe. Pruebe con otro.",
  "quote.discount.failed": "Hubo un error al aplicar el código. Vuelva a intentarlo.",
  "quote.discount.tooMany": "Se probaron demasiados códigos. Espere un rato y vuelva a intentarlo.",
  "quote.error.number": "No pudimos obtener un número de cotización de nuestros servidores. Revise su conexión a internet y vuelva a intentarlo. Si vuelve a ocurrir, escríbanos a support@everbary.com.",
  "quote.error.rejected": "Nuestro sistema no aceptó los datos de su solicitud. Es un problema de nuestro lado y volver a intentarlo no lo va a resolver. Escríbanos a support@everbary.com con su archivo y preparamos su cotización.",
  "quote.error.connection": "No pudimos conectarnos con nuestros servidores. Revise su conexión a internet y vuelva a intentarlo. Lo que ingresó sigue aquí.",
  "quote.error.other": "Algo falló de nuestro lado. Vuelva a intentarlo. Si vuelve a ocurrir, escríbanos a support@everbary.com con su archivo y preparamos su cotización.",
  "quote.error.clock": "La fecha o la hora de su dispositivo están mal y por eso nuestro sistema no puede aceptar su solicitud. Corríjalas en la configuración del dispositivo y vuelva a intentarlo.",
  "quote.error.address": "Ocurrió un error y no pudimos guardar la dirección.",
  "quote.missingFiles": ({ names }) => {
    const quoted = names.map((name) => `"${name}"`);
    const list = quoted.length > 1 ? `${quoted.slice(0, -1).join(", ")} y ${quoted[quoted.length - 1]}` : quoted[0];
    return names.length > 1
      ? `Los archivos ${list} ya no están cargados, así que los quitamos de su pedido de cotización. Vuelva a subirlos.`
      : `El archivo ${list} ya no está cargado, así que lo quitamos de su pedido de cotización. Vuelva a subirlo.`;
  },

  "quote.done.title": ({ name }) => (name ? <>Gracias, <NoTranslate>{name}</NoTranslate>. Recibimos su pedido de cotización.</> : "Gracias. Recibimos su pedido de cotización."),
  "quote.done.number": ({ number }) => <>Cotización <NoTranslate as="b">#{number}</NoTranslate></>,
  "quote.done.text": "Puede seguir su estado en la página de la cotización, en su cuenta. Le enviaremos un correo electrónico apenas calculemos el costo.",
  "quote.done.goToQuote": "Ver la cotización",

  "quote.draft.sentTitle": "Ya enviado",
  "quote.draft.sentText": ({ number }) => `El pedido de cotización #${number} ya fue enviado. Aquí lo tiene.`,
  "quote.draft.signInTitle": "Inicie sesión para continuar",
  "quote.draft.signInText": ({ number }) => `El pedido de cotización #${number} se empezó desde una cuenta. Inicie sesión con esa cuenta para continuarlo, o empiece uno nuevo aquí.`,
};

const pt = {
  "quote.step.services": "Serviços",
  "quote.step.contact": "Seus dados",
  "quote.step.created": "Orçamento enviado",

  "quote.details.emailText": "Avisaremos você por e-mail assim que seu orçamento estiver pronto.",

  "quote.button.continueServices": "Continuar para Serviços",
  "quote.button.backOptions": "Voltar para Serviços",
  "quote.button.complete": "Pedir orçamento",
  "quote.button.completing": "Enviando...",

  "quote.bar.label": "Seu orçamento",
  "quote.bar.free": "Grátis",
  "quote.bar.noObligation": "Sem compromisso de compra",
  "quote.bar.files": ({ n }) => `${n} ${Number(n) === 1 ? "arquivo" : "arquivos"}`,

  "quote.expect.title": "O QUE ESPERAR",
  "quote.expect.text": "Respondemos rápido com um orçamento formal que indica o custo e o prazo de entrega da sua tradução.",
  "quote.expect.free": "Orçamento grátis, sem compromisso de compra",
  "quote.expect.final": "Preço final, sem surpresas",
  "quote.expect.turnaround": "Prazo de entrega estimado",
  "quote.expect.convert": "Vira pedido em um passo, quando você quiser",
  "quote.expect.guaranteed": "O preço do orçamento vale por 30 dias",

  "quote.services.expeditedText": "Seu pedido tem prioridade e o prazo da entrega digital é reduzido em 50%.",
  "quote.services.expeditedPlusText": "Seu pedido tem prioridade e o prazo da entrega digital é reduzido em 83,33%.",
  "quote.services.similarClients": "Encontramos clientes parecidos na sua conta. Deseja escolher um deles para este orçamento?",
  "quote.services.clientQuotes": ({ n }) => `(${n} ${Number(n) === 1 ? "orçamento" : "orçamentos"})`,

  "quote.discount.title": "Código de desconto",
  "quote.discount.applying": "Aplicando...",
  "quote.discount.noLongerValid": "Esse código não é mais válido. Tente outro.",
  "quote.discount.ownTeam": "Esse é o código da sua equipe para os clientes dela. Ele não funciona nos seus próprios orçamentos.",
  "quote.discount.alreadyUsed": "Você já usou esse código. Ele vale uma única vez por cliente.",
  "quote.discount.notFound": "Esse código não existe. Tente outro.",
  "quote.discount.failed": "Houve um erro ao aplicar o código. Tente novamente.",
  "quote.discount.tooMany": "Foram testados códigos demais. Aguarde um pouco e tente novamente.",
  "quote.error.number": "Não conseguimos obter um número de orçamento dos nossos servidores. Verifique sua conexão com a internet e tente novamente. Se acontecer de novo, escreva para support@everbary.com.",
  "quote.error.rejected": "Nosso sistema não aceitou os dados da sua solicitação. O problema é do nosso lado e tentar de novo não vai resolver. Escreva para support@everbary.com com seu arquivo e nós preparamos seu orçamento.",
  "quote.error.connection": "Não conseguimos nos conectar aos nossos servidores. Verifique sua conexão com a internet e tente novamente. O que você preencheu continua aqui.",
  "quote.error.other": "Algo deu errado do nosso lado. Tente novamente. Se acontecer de novo, escreva para support@everbary.com com seu arquivo e nós preparamos seu orçamento.",
  "quote.error.clock": "A data ou a hora do seu dispositivo está errada e, por isso, nosso sistema não consegue aceitar sua solicitação. Corrija nas configurações do dispositivo e tente novamente.",
  "quote.error.address": "Ocorreu um erro e não conseguimos salvar o endereço.",
  "quote.missingFiles": ({ names }) => {
    const quoted = names.map((name) => `"${name}"`);
    const list = quoted.length > 1 ? `${quoted.slice(0, -1).join(", ")} e ${quoted[quoted.length - 1]}` : quoted[0];
    return names.length > 1
      ? `Os arquivos ${list} não estão mais enviados, então nós os removemos da sua solicitação de orçamento. Envie-os de novo.`
      : `O arquivo ${list} não está mais enviado, então nós o removemos da sua solicitação de orçamento. Envie-o de novo.`;
  },

  "quote.done.title": ({ name }) => (name ? <>Obrigado, <NoTranslate>{name}</NoTranslate>. Recebemos sua solicitação de orçamento.</> : "Obrigado. Recebemos sua solicitação de orçamento."),
  "quote.done.number": ({ number }) => <>Orçamento <NoTranslate as="b">#{number}</NoTranslate></>,
  "quote.done.text": "Você pode acompanhar o status na página do orçamento, na sua conta. Enviaremos um e-mail assim que calcularmos o custo.",
  "quote.done.goToQuote": "Ver o orçamento",

  "quote.draft.sentTitle": "Já enviado",
  "quote.draft.sentText": ({ number }) => `A solicitação de orçamento #${number} já foi enviada. Aqui está ela.`,
  "quote.draft.signInTitle": "Entre para continuar",
  "quote.draft.signInText": ({ number }) => `A solicitação de orçamento #${number} foi iniciada a partir de uma conta. Entre com essa conta para continuar, ou comece uma nova aqui.`,
};

const texts = { en, es, pt };

export default texts;
